import {
  Box,
  Button,
  Divider,
  IconButton,
  MenuItem,
  Tab,
  Tabs,
} from "@mui/material";
import { GridColDef, GridRowSelectionModel } from "@mui/x-data-grid";
import { useMutation, useQuery } from "@tanstack/react-query";
import { GKClientDataGrid, GKSelect, Grid2 } from "components";
import { CloseIcon, StarFillIcon, StarIcon } from "components/Icons";
import dayjs from "dayjs";
import DashboardLayout from "layouts/DashboardLayout";
import { useState } from "react";
import toast from "react-hot-toast";
import { useNavigate, useSearchParams } from "react-router-dom";
import axiosInstance from "utils/axiosInstance";
import { MF_CATEGORIES } from "utils/constants";
import { extraColumnsField } from "utils/helpers";
import {
  OTHER_FIELDS,
  PeriodToggle,
  RISK_HORIZONS,
  RISK_RATIOS,
  SectionAccordion,
  TRAILING_PERIODS,
  alphaRowClassName,
  getOtherValues,
  getRollingTable,
  topTenClassName,
  topTenSx,
} from "./Returns";

// Returns and expense ratio already come as percent values
const formatValue = (value?: number | string | null) =>
  value === null || value === undefined || value === "" ? "-" : value;

// Favourites are saved by gcode; fall back to schemeCode if gcode is missing
const getFundCode = (row: any) => String(row?.gcode ?? row?.schemeCode ?? "");

const MF = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // Coming back from the returns page keeps the last category selected
  const [selectedCategory, setSelectedCategory] = useState(
    searchParams.get("category") || ""
  );
  const [selectedRows, setSelectedRows] = useState<GridRowSelectionModel>([]);
  const [activeTab, setActiveTab] = useState<"favourite" | "screener">(
    "screener"
  );
  const [favouriteTab, setFavouriteTab] = useState<
    "returns" | "categoryReturns" | "riskRatios"
  >("categoryReturns");
  const [showYearly, setShowYearly] = useState(false);

  const { data, isLoading, isFetching } = useQuery({
    queryKey: ["mf-category-table", selectedCategory],
    queryFn: () =>
      // encodeURIComponent sends spaces as %20 (axios params would use +)
      axiosInstance.get(
        `/mf-screener/category-table?category=${encodeURIComponent(
          selectedCategory
        )}`
      ),
    enabled: !!selectedCategory,
    // Reuse the cached table when coming back from the returns page
    staleTime: Infinity,
    onError: (error: any) => {
      toast.error(error?.response?.data?.message || "Something went wrong");
    },
  });

  const { data: favouritesData, refetch: refetchFavourites } = useQuery({
    queryKey: ["mf-favourites"],
    queryFn: () => axiosInstance.get(`/mf-screener/favourites`),
  });

  // Accept either a list of gcodes or a list of fund objects
  const favouriteList: any[] =
    favouritesData?.data?.data || favouritesData?.data?.favourites || [];
  const favouriteCodes = new Set(
    favouriteList.map((item) =>
      String(typeof item === "object" ? item?.gcode : item)
    )
  );

  // Star adds the scheme to favourites, unstar removes it
  const { mutate: toggleFavourite, isLoading: isTogglingFavourite } =
    useMutation({
      mutationFn: ({
        gcode,
        isFavourite,
      }: {
        gcode: string;
        isFavourite: boolean;
      }) =>
        isFavourite
          ? axiosInstance.delete(`/mf-screener/favourites/${gcode}`)
          : axiosInstance.post(`/mf-screener/favourites`, { gcode }),
      onSuccess: (response) => {
        refetchFavourites();
        toast.success(response?.data?.message || "Favourites updated");
      },
      onError: (error: any) => {
        toast.error(error?.response?.data?.message || "Something went wrong");
      },
    });

  const response = data?.data;
  const rows: any[] = response?.funds || [];
  const tabRows =
    activeTab === "favourite"
      ? rows.filter((row) => favouriteCodes.has(getFundCode(row)))
      : rows;

  const benchmarkLabel = response?.benchmark?.name || response?.meta?.benchmark;

  // Benchmark and Category Median stay pinned below the scheme rows
  const pinnedBottomRows = response
    ? [
        {
          schemeCode: "benchmark",
          name: benchmarkLabel ? `Benchmark (${benchmarkLabel})` : "Benchmark",
          returns: response?.benchmark?.returns,
        },
        {
          schemeCode: "category-median",
          name: "Category Median",
          returns: response?.categoryMedian?.returns,
        },
      ]
    : [];

  const returnColumn = (label: string, key: string): GridColDef => ({
    ...extraColumnsField(label, key, 110, 0),
    valueGetter: (row) => formatValue(row?.row?.returns?.[key]),
    cellClassName: topTenClassName(key),
  });

  const favouriteColumn: GridColDef = {
    field: "favourite",
    headerName: "",
    width: 50,
    sortable: false,
    disableColumnMenu: true,
    renderCell: (params) => {
      const gcode = getFundCode(params.row);
      // Benchmark, Category Median and Alpha rows can't be favourited
      if (
        !gcode ||
        ["benchmark", "category-median"].includes(gcode) ||
        params.row.isAlpha
      )
        return null;
      const isFavourite = favouriteCodes.has(gcode);
      return (
        <IconButton
          disabled={isTogglingFavourite}
          onClick={(e) => {
            e.stopPropagation();
            toggleFavourite({ gcode, isFavourite });
          }}
        >
          {isFavourite ? (
            <StarFillIcon width={18} height={18} />
          ) : (
            <StarIcon width={18} height={18} />
          )}
        </IconButton>
      );
    },
  };

  const nameColumn: GridColDef = {
    ...extraColumnsField("Scheme Name", "name", 300, 2),
  };

  const columns: GridColDef[] = [
    favouriteColumn,
    nameColumn,
    {
      ...extraColumnsField("Category", "category", 140, 1),
      valueGetter: (row) => formatValue(row?.row?.category),
    },
    {
      ...extraColumnsField("Launch Date", "launchDate", 120, 0),
      valueGetter: (row) =>
        row?.row?.launchDate
          ? dayjs(row.row.launchDate).format("DD-MM-YYYY")
          : "-",
    },
    {
      ...extraColumnsField("AUM (Crore)", "aumCrore", 120, 0),
      valueGetter: (row) => formatValue(row?.row?.aumCrore),
    },
    {
      ...extraColumnsField("Expense Ratio (%)", "expenseRatio", 140, 0),
      valueGetter: (row) => formatValue(row?.row?.expenseRatio),
    },
    {
      ...extraColumnsField("Exit Load (Days)", "exitLoadDays", 130, 0),
      valueGetter: (row) => formatValue(row?.row?.exitLoadDays),
    },
    returnColumn("1-Year Ret (%)", "1y"),
    returnColumn("3-Yrs Ret (%)", "3y"),
    returnColumn("5-Yrs Ret (%)", "5y"),
    returnColumn("10-Yrs Ret (%)", "10y"),
    returnColumn("Since Launch Ret (%)", "sinceInception"),
  ];

  // Favourite > Returns: calendar returns of each fund, quarterly by default.
  // Toggle on = yearly (meta.years), off = quarterly (meta.quarters)
  const calendarPeriods: string[] =
    (showYearly ? response?.meta?.years : response?.meta?.quarters) || [];
  const calendarKey = showYearly ? "calendarReturns" : "quarterlyReturns";
  const categoryReturnColumns: GridColDef[] = [
    favouriteColumn,
    nameColumn,
    ...calendarPeriods.map((period) => ({
      ...extraColumnsField(period, period, 90, 0),
      valueGetter: (row: any) => formatValue(row?.row?.[calendarKey]?.[period]),
    })),
  ];

  // Favourite > Risk Ratios: risk.<ratio> comes keyed by horizon in years
  const riskRatioColumns: GridColDef[] = [
    favouriteColumn,
    nameColumn,
    ...RISK_RATIOS.flatMap((ratio) =>
      RISK_HORIZONS.map((horizon) => ({
        ...extraColumnsField(
          `${ratio.label} ${horizon}y`,
          `${ratio.key}-${horizon}`,
          120,
          0
        ),
        valueGetter: (row: any) =>
          formatValue(row?.row?.risk?.[ratio.key]?.[horizon]),
      }))
    ),
  ];

  // Favourite > Returns: trailing returns, shown above the calendar year table
  const trailingColumns: GridColDef[] = [
    favouriteColumn,
    nameColumn,
    ...TRAILING_PERIODS.map((period) => returnColumn(period.label, period.key)),
  ];

  const isFavouriteTab = activeTab === "favourite";
  const tabColumns = !isFavouriteTab
    ? columns
    : {
        returns: trailingColumns,
        // Same table as MF Screener without Category, only favourite funds
        categoryReturns: columns.filter(
          (column) => column.field !== "category"
        ),
        riskRatios: riskRatioColumns,
      }[favouriteTab];
  const showCalendarReturns = isFavouriteTab && favouriteTab === "returns";
  const showOtherReturns = isFavouriteTab && favouriteTab === "riskRatios";

  // Favourite > Risk Ratios: drawdown, capture and valuation, below the ratios
  const otherColumns: GridColDef[] = [
    favouriteColumn,
    nameColumn,
    ...OTHER_FIELDS.map((field) => ({
      ...extraColumnsField(field.label, field.key, 130, 0),
      valueGetter: (row: any) => formatValue(row?.row?.[field.key]),
    })),
  ];
  const otherRows = tabRows.map((fund) => ({
    ...fund,
    ...getOtherValues(fund),
  }));

  // Favourite > Returns: rolling returns, one accordion per horizon
  const { rollingColumns, rollingGrouping } = getRollingTable(response, [
    favouriteColumn,
    nameColumn,
  ]);
  const getRollingRows = (horizon: string) =>
    tabRows.map((fund) => ({
      ...fund?.rolling?.[horizon],
      schemeCode: fund.schemeCode,
      gcode: fund.gcode,
      name: fund.name,
    }));

  // Favourite > Returns: an Alpha row under each scheme in the trailing table
  const gridRows = showCalendarReturns
    ? tabRows.flatMap((fund) => [
        fund,
        {
          schemeCode: `${fund.schemeCode}-alpha`,
          name: "Alpha",
          returns: fund?.alpha,
          topTen: fund?.alphaTopTen,
          isAlpha: true,
        },
      ])
    : tabRows;

  // Top ten return cells show green wherever this grid is used
  const mainGrid = (
    <Box sx={topTenSx}>
      <GKClientDataGrid
        // Remount on column set change so widths/sorting don't carry over
        key={isFavouriteTab ? favouriteTab : "screener"}
        columns={tabColumns}
        rows={gridRows}
        getRowClassName={alphaRowClassName}
        getRowId={(row) => row.schemeCode}
        loading={isLoading || isFetching}
        showFiltersButton={false}
        // Selecting funds for Submit is only on the screener tab
        checkboxSelection={!isFavouriteTab}
        disableRowSelectionOnClick
        isRowSelectable={(params) =>
          !["category-median", "benchmark"].includes(String(params.id))
        }
        rowSelectionModel={selectedRows}
        onRowSelectionModelChange={setSelectedRows}
        // Benchmark and Category Median only on the screener tab
        pinnedRows={{ bottom: isFavouriteTab ? [] : pinnedBottomRows }}
      />
    </Box>
  );

  return (
    <DashboardLayout title="MF">
      <Grid2 container spacing={1}>
        <Grid2 size={{ xs: 12, md: 3 }}>
          <GKSelect
            size="small"
            inputLabel="Category"
            value={selectedCategory}
            onChange={(e) => {
              setSelectedCategory(e.target.value as string);
              setSelectedRows([]);
            }}
            endAdornment={
              selectedCategory && (
                <span
                  role="button"
                  tabIndex={0}
                  style={{
                    display: "flex",
                    cursor: "pointer",
                    alignItems: "center",
                    paddingRight: 8,
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    setSelectedCategory("");
                    setSelectedRows([]);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.stopPropagation();
                      setSelectedCategory("");
                      setSelectedRows([]);
                    }
                  }}
                >
                  <CloseIcon />
                </span>
              )
            }
          >
            {MF_CATEGORIES.map((category) => (
              <MenuItem key={category} value={category}>
                {category}
              </MenuItem>
            ))}
          </GKSelect>
        </Grid2>
        {selectedRows.length > 0 && (
          <Grid2
            size={{ xs: 12, md: 9 }}
            display="flex"
            alignItems="center"
            justifyContent="flex-end"
          >
            <Button
              variant="contained"
              // Pass the already fetched data so the next page needs no API call
              onClick={() =>
                navigate("/app/mf/returns", {
                  state: {
                    category: selectedCategory,
                    response: {
                      ...response,
                      funds: rows.filter((fund) =>
                        selectedRows.includes(fund.schemeCode)
                      ),
                    },
                  },
                })
              }
            >
              Submit ({selectedRows.length})
            </Button>
          </Grid2>
        )}
        {selectedCategory && (
          <Grid2 size={12}>
            <Tabs
              value={activeTab}
              onChange={(_, value) => {
                setActiveTab(value);
                setSelectedRows([]);
              }}
            >
              <Tab disableRipple label="Favourite" value="favourite" />
              <Tab disableRipple label="MF Screener" value="screener" />
            </Tabs>
            <Divider />
          </Grid2>
        )}
        {selectedCategory && isFavouriteTab && (
          <Grid2 size={12}>
            <Tabs
              value={favouriteTab}
              onChange={(_, value) => setFavouriteTab(value)}
            >
              <Tab
                disableRipple
                label="Category Returns"
                value="categoryReturns"
              />
              <Tab disableRipple label="Returns" value="returns" />
              <Tab disableRipple label="Risk Ratios" value="riskRatios" />
            </Tabs>
            <Divider />
          </Grid2>
        )}
        {selectedCategory && !showCalendarReturns && !showOtherReturns && (
          <Grid2 size={12}>{mainGrid}</Grid2>
        )}
        {/* Favourite > Risk Ratios: same accordions as the returns page */}
        {selectedCategory && showOtherReturns && (
          <>
            <Grid2 size={12}>
              <SectionAccordion title="Sortino / Information / Sharpe">
                {mainGrid}
              </SectionAccordion>
            </Grid2>
            <Grid2 size={12}>
              <SectionAccordion title="Others Returns">
                <GKClientDataGrid
                  columns={otherColumns}
                  rows={otherRows}
                  getRowId={(row) => row.schemeCode}
                  loading={isLoading || isFetching}
                  showFiltersButton={false}
                  disableRowSelectionOnClick
                />
              </SectionAccordion>
            </Grid2>
          </>
        )}
        {/* Favourite > Returns: same accordions as the returns page */}
        {selectedCategory && showCalendarReturns && (
          <>
            <Grid2 size={12}>
              <SectionAccordion title="Trailing Returns">
                {mainGrid}
              </SectionAccordion>
            </Grid2>
            <Grid2 size={12}>
              <SectionAccordion title="Calendar Year Returns">
                <PeriodToggle checked={showYearly} onChange={setShowYearly} />
                <GKClientDataGrid
                  // Remount when switching so the new period columns load fresh
                  key={calendarKey}
                  columns={categoryReturnColumns}
                  // No periods from the API (e.g. no quarters) shows an empty table
                  rows={calendarPeriods.length ? tabRows : []}
                  getRowId={(row) => row.schemeCode}
                  loading={isLoading || isFetching}
                  showFiltersButton={false}
                  disableRowSelectionOnClick
                />
              </SectionAccordion>
            </Grid2>
            {["3", "5"].map((horizon) => (
              <Grid2 size={12} key={horizon}>
                <SectionAccordion title={`Rolling Return - ${horizon} yr`}>
                  <GKClientDataGrid
                    columns={rollingColumns}
                    rows={getRollingRows(horizon)}
                    getRowId={(row) => row.schemeCode}
                    loading={isLoading || isFetching}
                    showFiltersButton={false}
                    disableRowSelectionOnClick
                    columnGroupingModel={rollingGrouping}
                  />
                </SectionAccordion>
              </Grid2>
            ))}
          </>
        )}
      </Grid2>
    </DashboardLayout>
  );
};

export default MF;
